import { PICK_EVENT_TYPES, PICKER_IDLE_GAP_MS, PICKING_STATUS, WAVE_STATUS } from "../constants/PickingConstant";
import { parseDate } from "./pickDay";
import { projectPickEvents, sortPickEvents, toMillis } from "./pickEvents";

/**
 * The picker report: per picker how many orders, lines and units, and how long - per order, per
 * round and in total.
 *
 * Read from the pick events of each round, not from `orders/{id}.picking`: the events say who did
 * what and when, and a pause exists only there. What was picked comes from the same fold the CMS
 * writes onto the orders (functions/pickEvents), so the report and the orders never disagree on it.
 *
 * Time is cut into spans, per scanner, from one event to the next:
 *  - "work", for the picker of the later event: walking to the vak, picking, scanning;
 *  - "pause", from CMD-PAUSE to CMD-PAUSE (wave_paused ... wave_resumed);
 *  - "idle", a gap of more than PICKER_IDLE_GAP_MS without a scan: a break without the pause
 *    command. It does not count as picking time.
 * Per scanner, because two scanners on one round (after a takeover) each have their own clock and
 * their own gaps; their work adds up, as time people spent.
 *
 * An order's time is the work from scanning its slip until it is closed. The span that ends at the
 * slip scan is the change-over from the order before (box away, next slip): it counts for the
 * round, not for the order. A round's time is all of its work.
 *
 * An order belongs to the picker who closed it. The app refuses a badge switch in the middle of an
 * order, so that is also who picked it.
 *
 * Pure: no Firestore, no React.
 */

const { ORDER_STARTED, ORDER_COMPLETED, ORDER_ABORTED, WAVE_PAUSED, WAVE_RESUMED, LOCATION_EMPTY } = PICK_EVENT_TYPES;
const TERMINAL = new Set([ORDER_COMPLETED, ORDER_ABORTED]);
const CLOSED = new Set([PICKING_STATUS.PICKED, PICKING_STATUS.PARTIAL]);
const HOUR_MS = 60 * 60 * 1000;

export const SPAN_KIND = { WORK: "work", PAUSE: "pause", IDLE: "idle" };

/**
 * @returns {Array<{deviceId, pickerId, orderRef, from, to, ms, kind}>} one span per pair of
 *   consecutive events on a scanner; `orderRef` is the order being picked during the span, or null.
 */
export const timeSpans = (events, { idleGapMs = PICKER_IDLE_GAP_MS } = {}) => {
  const streams = new Map();
  sortPickEvents(events).forEach((event) => {
    const at = toMillis(event.at);
    if (at === null) return;
    const deviceId = event.deviceId || null;
    if (!streams.has(deviceId)) streams.set(deviceId, []);
    streams.get(deviceId).push({ event, at });
  });

  const spans = [];
  streams.forEach((stream, deviceId) => {
    // seq counts up on a scanner even when its clock is set back: the stream is in seq order
    stream.sort((a, b) => (a.event.seq || 0) - (b.event.seq || 0));
    let paused = false;
    let openOrder = null;
    stream.forEach(({ event, at }, index) => {
      if (index > 0) {
        const from = stream[index - 1].at;
        const ms = Math.max(0, at - from);
        spans.push({
          deviceId,
          pickerId: event.pickerId || null,
          // The change-over to an order is not part of it; a pause or the finish during one is
          orderRef: event.type === ORDER_STARTED ? null : event.orderRef || openOrder,
          from,
          to: at,
          ms,
          kind: paused ? SPAN_KIND.PAUSE : ms > idleGapMs ? SPAN_KIND.IDLE : SPAN_KIND.WORK,
        });
      }
      if (event.type === WAVE_PAUSED) paused = true;
      if (event.type === WAVE_RESUMED) paused = false;
      if (event.type === ORDER_STARTED) openOrder = event.orderRef || null;
      if (TERMINAL.has(event.type)) openOrder = null;
    });
  });
  return spans;
};

const total = (items, pick) => items.reduce((sum, item) => sum + (pick(item) || 0), 0);

/**
 * One round: when, how long, by whom, and per order who picked it, how much and how long.
 *
 * @param {Object} wave - the wave document (`orders[]` with orderRef, orderId, shopSource, sequence)
 * @param {Object[]} events - all of its events
 */
export const summariseWave = (wave, events, { idleGapMs = PICKER_IDLE_GAP_MS } = {}) => {
  const projection = projectPickEvents(wave, events);
  const spans = timeSpans(events, { idleGapMs });
  const work = spans.filter((span) => span.kind === SPAN_KIND.WORK);

  const orderWork = new Map();
  const workByPicker = {};
  work.forEach((span) => {
    if (span.orderRef) orderWork.set(span.orderRef, (orderWork.get(span.orderRef) || 0) + span.ms);
    if (span.pickerId) workByPicker[span.pickerId] = (workByPicker[span.pickerId] || 0) + span.ms;
  });

  const orders = ((wave && wave.orders) || []).map((waveOrder) => {
    const state = projection.orders[waveOrder.orderRef];
    const lines = Object.values(state.lines);
    return {
      orderRef: waveOrder.orderRef,
      orderId: waveOrder.orderId || null,
      shopSource: waveOrder.shopSource || null,
      sequence: waveOrder.sequence,
      status: state.status,
      pickerId: state.pickerId,
      startedAt: state.startedAt,
      completedAt: state.completedAt,
      workMs: orderWork.get(waveOrder.orderRef) || 0,
      lines: lines.length,
      linesPicked: lines.filter((line) => line.picked > 0).length,
      units: total(lines, (line) => line.picked),
      unitsExpected: total(lines, (line) => line.expected),
      manualUnits: total(lines, (line) => line.manual),
      emptyLocations: state.exceptions.filter((exception) => exception.type === LOCATION_EMPTY).length,
    };
  });

  const sorted = sortPickEvents(events);
  const times = sorted.map((event) => toMillis(event.at)).filter((at) => at !== null);
  const pickers = [];
  sorted.forEach((event) => {
    if (event.pickerId && !pickers.includes(event.pickerId)) pickers.push(event.pickerId);
  });
  const closed = orders.filter((order) => CLOSED.has(order.status));

  return {
    waveId: wave.waveId || wave.id,
    waveNumber: wave.waveNumber === undefined ? null : wave.waveNumber,
    pickDay: wave.pickDay || null,
    // What the events say, which is ahead of the stored status until the round is processed
    status: wave.status === WAVE_STATUS.CANCELLED ? WAVE_STATUS.CANCELLED : projection.waveStatus,
    startedAt: times.length > 0 ? times.reduce((a, b) => Math.min(a, b)) : null,
    endedAt: times.length > 0 ? times.reduce((a, b) => Math.max(a, b)) : null,
    workMs: total(work, (span) => span.ms),
    pauseMs: total(spans.filter((span) => span.kind === SPAN_KIND.PAUSE), (span) => span.ms),
    idleMs: total(spans.filter((span) => span.kind === SPAN_KIND.IDLE), (span) => span.ms),
    pickers,
    workByPicker,
    orders,
    orderCount: orders.length,
    closedCount: closed.length,
    abortedCount: orders.filter((order) => order.status === PICKING_STATUS.ABORTED).length,
    linesPicked: total(orders, (order) => order.linesPicked),
    units: total(orders, (order) => order.units),
    avgOrderMs: closed.length > 0 ? Math.round(total(closed, (order) => order.workMs) / closed.length) : null,
    eventCount: projection.eventCount,
  };
};

/** Rounds in date order: pick day, then round number. */
export const sortWaveSummaries = (waves) =>
  [...(waves || [])].sort((a, b) => {
    const dayA = parseDate(a.pickDay);
    const dayB = parseDate(b.pickDay);
    return (dayA ? dayA.getTime() : 0) - (dayB ? dayB.getTime() : 0) || (a.waveNumber || 0) - (b.waveNumber || 0);
  });

const byCode = (a, b) => a.code.localeCompare(b.code, "nl", { numeric: true });

/**
 * Per picker over a set of rounds (from summariseWave), with the name from the register.
 *
 * Every badge in the register gets a row, also without activity - the table on the Pickers page is
 * the register as well - and a code that picked but is not in the register gets one with
 * `registered: false`.
 */
export const summarisePickers = (waveSummaries, pickers = []) => {
  const rows = new Map();
  const rowFor = (code) => {
    if (!rows.has(code)) {
      rows.set(code, {
        code,
        waveIds: new Set(),
        workMs: 0,
        orders: 0,
        partialOrders: 0,
        abortedOrders: 0,
        orderWorkMs: 0,
        linesPicked: 0,
        units: 0,
        manualUnits: 0,
        emptyLocations: 0,
      });
    }
    return rows.get(code);
  };

  (waveSummaries || []).forEach((wave) => {
    Object.entries(wave.workByPicker || {}).forEach(([code, ms]) => {
      const row = rowFor(code);
      row.workMs += ms;
      row.waveIds.add(wave.waveId);
    });
    (wave.orders || []).forEach((order) => {
      if (!order.pickerId) return;
      const row = rowFor(order.pickerId);
      row.waveIds.add(wave.waveId);
      if (CLOSED.has(order.status)) {
        row.orders += 1;
        row.orderWorkMs += order.workMs;
        if (order.status === PICKING_STATUS.PARTIAL) row.partialOrders += 1;
      }
      if (order.status === PICKING_STATUS.ABORTED) row.abortedOrders += 1;
      row.linesPicked += order.linesPicked;
      row.units += order.units;
      row.manualUnits += order.manualUnits;
      row.emptyLocations += order.emptyLocations;
    });
  });

  const register = new Map((pickers || []).map((picker) => [picker.code, picker]));
  register.forEach((_, code) => rowFor(code));

  return [...rows.values()]
    .map(({ waveIds, ...row }) => {
      const picker = register.get(row.code);
      return {
        ...row,
        name: picker ? picker.name || "" : null,
        registered: !!picker,
        active: picker ? picker.active !== false : true,
        rounds: waveIds.size,
        hasActivity: waveIds.size > 0 || row.workMs > 0,
        avgOrderMs: row.orders > 0 ? Math.round(row.orderWorkMs / row.orders) : null,
        unitsPerHour: row.workMs > 0 ? row.units / (row.workMs / HOUR_MS) : null,
      };
    })
    .sort(byCode);
};

/** The sums over all pickers, for the totals row. */
export const totalPickerRows = (rows) => {
  const workMs = total(rows, (row) => row.workMs);
  const orders = total(rows, (row) => row.orders);
  const units = total(rows, (row) => row.units);
  return {
    orders,
    partialOrders: total(rows, (row) => row.partialOrders),
    abortedOrders: total(rows, (row) => row.abortedOrders),
    linesPicked: total(rows, (row) => row.linesPicked),
    units,
    manualUnits: total(rows, (row) => row.manualUnits),
    emptyLocations: total(rows, (row) => row.emptyLocations),
    workMs,
    avgOrderMs: orders > 0 ? Math.round(total(rows, (row) => row.orderWorkMs) / orders) : null,
    unitsPerHour: workMs > 0 ? units / (workMs / HOUR_MS) : null,
  };
};

const pad = (number) => String(number).padStart(2, "0");

/** 45 s · 4 min 05 s · 1 u 05 min; "-" for nothing. */
export const formatDuration = (ms) => {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "-";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ${pad(seconds % 60)} s`;
  return `${Math.floor(minutes / 60)} u ${pad(minutes % 60)} min`;
};

/** Minutes with one decimal, as a number: what a spreadsheet can add up. */
export const toMinutes = (ms) => (ms === null || ms === undefined ? null : Math.round(ms / 6000) / 10);

const clock = (ms) => {
  if (ms === null || ms === undefined) return "";
  const date = new Date(ms);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const sheet = (sheetName, titles, rows) => ({
  sheetName,
  columns: titles.map((title) => ({ title })),
  data: rows.map((row) => row.map((value) => ({ value: value === null || value === undefined ? "" : value }))),
});

/**
 * The report as three sheets for components/shared-components/ExcelExport: per picker, per round
 * and per order. Times in minutes, as numbers, so they can be summed and averaged.
 */
export const buildPickerReportSheets = ({ pickerRows, waves, nameOf = () => null }) => {
  const who = (code) => (code ? [code, nameOf(code) || ""] : ["", ""]);
  return [
    sheet(
      "Pickers",
      ["Pasje", "Naam", "Rondes", "Orders", "Waarvan niet compleet", "Afgebroken", "Regels", "Stuks", "Handmatig bevestigd", "Lege vakken", "Picktijd (min)", "Per order (min)", "Stuks per uur"],
      pickerRows.map((row) => [
        row.code,
        row.name || "",
        row.rounds,
        row.orders,
        row.partialOrders,
        row.abortedOrders,
        row.linesPicked,
        row.units,
        row.manualUnits,
        row.emptyLocations,
        toMinutes(row.workMs),
        toMinutes(row.avgOrderMs),
        row.unitsPerHour === null ? null : Math.round(row.unitsPerHour),
      ]),
    ),
    sheet(
      "Rondes",
      ["Pickdag", "Ronde", "Status", "Pickers", "Orders", "Afgerond", "Regels", "Stuks", "Start", "Einde", "Picktijd (min)", "Pauze (min)", "Stil (min)", "Per order (min)"],
      waves.map((wave) => [
        wave.pickDay,
        wave.waveNumber,
        wave.status,
        wave.pickers.map((code) => nameOf(code) || code).join(", "),
        wave.orderCount,
        wave.closedCount,
        wave.linesPicked,
        wave.units,
        clock(wave.startedAt),
        clock(wave.endedAt),
        toMinutes(wave.workMs),
        toMinutes(wave.pauseMs),
        toMinutes(wave.idleMs),
        toMinutes(wave.avgOrderMs),
      ]),
    ),
    sheet(
      "Orders",
      ["Pickdag", "Ronde", "Volgorde", "Order", "Shop", "Pasje", "Naam", "Status", "Regels", "Stuks", "Verwacht", "Handmatig bevestigd", "Lege vakken", "Start", "Einde", "Tijd (min)"],
      waves.flatMap((wave) =>
        wave.orders.map((order) => [
          wave.pickDay,
          wave.waveNumber,
          order.sequence,
          order.orderId,
          order.shopSource,
          ...who(order.pickerId),
          order.status,
          order.linesPicked,
          order.units,
          order.unitsExpected,
          order.manualUnits,
          order.emptyLocations,
          clock(order.startedAt),
          clock(order.completedAt),
          order.startedAt === null ? null : toMinutes(order.workMs),
        ]),
      ),
    ),
  ];
};
